import { defineStore } from 'pinia'
import { nextTick } from 'vue'
import { DashboardService } from '@/services/api/dashboard'
import { useAuthStore } from '@/stores'
import { handleApiError } from '@/services/errorHandler.js'

const MOCK_DATA = {
  airline: {
    stats: [
      { id: 1, name: 'Request', count: 11, total: 16, status: 'Active', color: 'text-error' },
      { id: 2, name: 'Quotes', count: 4, total: 8, status: 'Accepted', color: 'text-primary' },
      { id: 3, name: 'Contracts', count: 2, total: 7, status: 'Active', color: 'text-success' },
      { id: 4, name: 'Shipments', count: 1, total: 2, status: 'Pending', color: 'text-warning' },
    ],
    acceptanceRate: {
      increase: '3.5',
      received: [4, 8, 14, 20, 26, 31, 35, 39, 42],   // 3 curves — airline only
      quoted:   [2, 4,  7, 11, 15, 19, 22, 25, 28],
      accepted: [1, 2,  3,  5,  7,  9, 11, 13, 15],
    },
    turnover: {
      currentYear:  [1200, 950, 1800, 2100, 890, 1650, 2300, 1400, 1750, 0, 0, 0],
      previousYear: [ 850, 944, 1102, 1302, 1150, 1200,  980,  870,  760, 900, 1050, 1100],
      numberOfFlights: 17,
      currency: '$',
      fee: 0.03,
    },
  },
  charterer: {
    stats: [
      { id: 1, name: 'Request', count: 22, total: 28, status: 'Active', color: 'text-error' },
      { id: 2, name: 'Quotes', count: 10, total: 13, status: 'Received', color: 'text-primary' },
      { id: 3, name: 'Contracts', count: 12, total: 23, status: 'Active', color: 'text-success' },
      { id: 4, name: 'Shipments', count: 2, total: 13, status: 'Pending', color: 'text-warning' },
    ],
    acceptanceRate: {
      increase: '7.5',
      sent:     [ 5,  9, 14, 20, 27, 33, 38, 43, 48],   // 2 curves — charterer/admin
      accepted: [ 2,  4,  7, 11, 14, 18, 22, 26, 30],
    },
    turnover: {
      currentYear:  [3200, 2800, 4500, 5100, 3800, 6200, 7100, 5500, 4900, 0, 0, 0],
      previousYear: [2800, 3100, 3900, 4700, 4200, 5800, 6400, 5100, 4300, 5200, 6100, 7800],
      numberOfFlights: 32,
      currency: '$',
      fee: null,
    },
  },
}

const EMPTY_ACCEPTANCE_RATE = { increase: '', sent: [], received: [], quoted: [], accepted: [] }

export const useDashboardStore = defineStore('dashboard', {
  state: () => ({
    useMock: true,
    loading: false,
    stats: [],
    acceptanceRate: { ...EMPTY_ACCEPTANCE_RATE },
    turnover: {
      currentYear: [],
      previousYear: [],
      numberOfFlights: 0,
      currency: '$',
      fee: null,
    },
  }),
  actions: {
    async fetchDashboardData() {
      const authStore = useAuthStore()
      this.loading = true
      this.stats = []
      this.acceptanceRate = { ...EMPTY_ACCEPTANCE_RATE }
      this.turnover = { currentYear: [], previousYear: [], numberOfFlights: 0, currency: '$', fee: null }
      try {
        let data
        if (this.useMock) {
          await nextTick()
          const userType = authStore.user?.user_type
          data = MOCK_DATA[userType] ?? MOCK_DATA.airline
        } else {
          const { status, data: apiData } = await DashboardService.dashboard()
          if (status !== 200 || !apiData) return
          data = apiData
        }
        this.stats = data.stats
        this.acceptanceRate = {
          increase: data.acceptanceRate.increase ?? '',
          sent:     data.acceptanceRate.sent      ?? [],
          received: data.acceptanceRate.received  ?? [],
          quoted:   data.acceptanceRate.quoted    ?? [],
          accepted: data.acceptanceRate.accepted  ?? [],
        }
        this.turnover = {
          ...data.turnover,
          fee: data.turnover.fee ?? null,
        }
      } catch (error) {
        handleApiError(error, { title: 'Failed to fetch dashboard', silent: true })
        this.stats = []
      } finally {
        this.loading = false
      }
    },
  },
})
